"use client";

import { useEffect, useRef } from "react";

const units = [
  {
    id: "academia-code",
    name: "Unidade Academia Code",
    address: "Av. Sertório, 8000",
    complement: "3º Andar do Centelar",
    neighborhood: "Sarandi – Porto Alegre – RS",
    whatsapp: "5551999991111",
    mapUrl: "https://maps.google.com/?q=Av.+Sertório,+8000,+Porto+Alegre",
    highlight: "Maior unidade",
    number: "01",
  },
  {
    id: "cristo-redentor",
    name: "Unidade Cristo Redentor",
    address: "Av. Assis Brasil, 2474",
    complement: "Sala 201/202",
    neighborhood: "Cristo Redentor – Porto Alegre – RS",
    whatsapp: "5551999992222",
    mapUrl: "https://maps.google.com/?q=Av.+Assis+Brasil,+2474,+Porto+Alegre",
    highlight: "Localização central",
    number: "02",
  },
  {
    id: "jardim-leopoldina",
    name: "Unidade Jardim Leopoldina",
    address: "Rua Prof.ª Amy Herve Ramires, 167",
    complement: "Loja 08 – J",
    neighborhood: "Jardim Dona Leopoldina – Porto Alegre – RS",
    whatsapp: "5551999993333",
    mapUrl: "https://maps.google.com/?q=Rua+Professora+Amy+Herve+Ramires,+167,+Porto+Alegre",
    highlight: "Ambiente tranquilo",
    number: "03",
  },
];

export default function UnitsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("reveal-hidden");
            entry.target.classList.add("reveal-active");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    const items = sectionRef?.current?.querySelectorAll(".reveal-hidden");
    items?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="unidades" className="py-32 md:py-40 px-6 md:px-10 bg-cream" ref={sectionRef}>
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20 reveal-hidden">
          <span className="subheading mb-5 block">Nossas Unidades</span>
          <h2 className="font-serif text-forest leading-tight mb-5"
            style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)' }}>
            Escolha a unidade mais próxima{" "}
            <span className="italic font-light text-sage">de você</span>
          </h2>
          <span className="section-rule mx-auto" />
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {units?.map((unit, i) => (
            <div
              key={unit?.id}
              className={`unit-card reveal-hidden reveal-delay-${i + 1} rounded-3xl overflow-hidden flex flex-col`}
              style={{
                background: 'var(--sage-pale)',
                minHeight: '320px',
                boxShadow: '0 4px 24px rgba(26,61,26,0.06), 0 12px 48px rgba(26,61,26,0.04)',
              }}
            >
              {/* Card top accent */}
              <div className="h-1.5 w-full" style={{ background: 'linear-gradient(to right, var(--forest), var(--sage))' }} />
              
              <div className="p-10 flex flex-col flex-1">
                {/* Number + badge */}
                <div className="flex items-start justify-between mb-8">
                  <span className="font-serif font-light" style={{ fontSize: '3.5rem', lineHeight: 1, color: 'var(--gold)', opacity: 0.5 }}>
                    {unit?.number}
                  </span>
                  <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: 'rgba(26,61,26,0.08)', color: 'var(--forest)' }}>
                    {unit?.highlight}
                  </span>
                </div>

                <h3 className="font-serif mb-6 leading-snug" style={{ fontSize: '1.4rem', color: 'var(--forest)' }}>
                  {unit?.name}
                </h3>

                {/* Address */}
                <div className="flex gap-3 mb-auto">
                  <div className="flex-shrink-0 mt-1">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--gold)' }}>
                      <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-sm" style={{ color: 'var(--charcoal)' }}>{unit?.address}</p>
                    <p className="text-sm" style={{ color: 'var(--stone)' }}>{unit?.complement}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--stone-light)' }}>{unit?.neighborhood}</p>
                  </div>
                </div>

                <div className="divider-gold my-8" />

                {/* Actions */}
                <div className="flex gap-3">
                  <a
                    href={`https://wa.me/${unit?.whatsapp}?text=Olá! Gostaria de saber mais sobre a ${unit?.name}.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 text-white rounded-full py-3.5 text-sm font-semibold transition-all duration-300 hover:opacity-90 hover:shadow-lg"
                    style={{ background: 'var(--forest)' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>
                  <a
                    href={unit?.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold transition-all duration-300 hover:bg-forest hover:text-cream"
                    style={{ border: '1.5px solid var(--forest)', color: 'var(--forest)' }}
                  >
                    Ver unidade
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}